import {
  SignUpContentFragment,
  SignUpImageFragment,
  GetRoleSignUpContentQuery,
} from '@sketch/gql-types'

interface Image {
  src: string
  alt: string
}

interface FeatureJson {
  icon: Image
  description: string
}

interface RelatedContentJson {
  title: string
  description: string
  thumbnail: Image
  url: string
}

interface SignUpContentJson {
  title: SignUpContentFragment['title']
  tint: SignUpContentFragment['tint']
  introduction: SignUpContentFragment['introduction']
  features: FeatureJson[]
  callToAction: SignUpContentFragment['callToAction']
  relatedContent?: SignUpContentFragment['relatedContent']
  heroImage?: SignUpContentFragment['heroImage']
}

interface RoleJson {
  designer: SignUpContentJson
  designSystemManager: SignUpContentJson
  managerOrDesignTeamLead: SignUpContentJson
  product: SignUpContentJson
  developer: SignUpContentJson
  marketingSpecialist: SignUpContentJson
  writer: SignUpContentJson
  financeOrAdmin: SignUpContentJson
  researcher: SignUpContentJson
  student: SignUpContentJson
  teacher: SignUpContentJson
  educationalInstitution: SignUpContentJson
  other: SignUpContentJson
}

export const roleContentFromJson = (
  json: RoleJson
): GetRoleSignUpContentQuery['roleSignUpContent'] => {
  return {
    __typename: 'RoleSignUpContent',
    designer: signUpContentFromJson(json.designer),
    designSystemManager: signUpContentFromJson(json.designSystemManager),
    managerOrDesignTeamLead: signUpContentFromJson(
      json.managerOrDesignTeamLead
    ),
    product: signUpContentFromJson(json.product),
    developer: signUpContentFromJson(json.developer),
    marketingSpecialist: signUpContentFromJson(json.marketingSpecialist),
    writer: signUpContentFromJson(json.writer),
    financeOrAdmin: signUpContentFromJson(json.financeOrAdmin),
    researcher: signUpContentFromJson(json.researcher),
    student: signUpContentFromJson(json.student),
    teacher: signUpContentFromJson(json.teacher),
    educationalInstitution: signUpContentFromJson(json.educationalInstitution),
    other: signUpContentFromJson(json.other),
  }
}

export const signUpContentFromJson = (
  json: SignUpContentJson
): SignUpContentFragment => {
  return {
    __typename: 'SignUpContent',
    title: json.title,
    tint: json.tint,
    introduction: json.introduction,
    features: json.features.map(featureFromJson),
    callToAction: json.callToAction,
    relatedContent: relatedContentFromJson(json.relatedContent),
    heroImage: heroFromJson(json.heroImage),
  }
}

const featureFromJson = (
  json: FeatureJson
): SignUpContentFragment['features'][0] => {
  return {
    __typename: 'SignUpFeature',
    icon: imageFromJson(json.icon),
    description: json.description,
  }
}

const imageFromJson = (json: Image): SignUpImageFragment => {
  return {
    __typename: 'SignUpImage',
    src: json.src,
    alt: json.alt,
  }
}

const relatedContentFromJson = (
  json: RelatedContentJson | null | undefined
): SignUpContentFragment['relatedContent'] => {
  if (json) {
    return {
      __typename: 'SignUpRelatedContent',
      title: json.title,
      description: json.description,
      thumbnail: {
        __typename: 'SignUpImage',
        src: json.thumbnail.src,
        alt: json.thumbnail.alt,
      },
      url: json.url,
    }
  }

  return null
}

const heroFromJson = (
  json: Image | null | undefined
): SignUpContentFragment['heroImage'] => {
  if (json) {
    return imageFromJson(json)
  }

  return null
}

export const gqlTintToLocalTint: {
  [K in SignUpContentFragment['tint']]:
    | 'orange'
    | 'teal'
    | 'beige'
    | 'purple'
    | 'green'
} = {
  ORANGE: 'orange',
  TEAL: 'teal',
  BEIGE: 'beige',
  PURPLE: 'purple',
  GREEN: 'green',
}
